export interface FigmaFrame {
  id: string;
  name: string;
}

export function parseFigmaFileKey(url: string): string | null {
  const match = url.match(/figma\.com\/(?:file|design|proto)\/([A-Za-z0-9]+)/);
  return match ? match[1] : null;
}

export async function fetchFigmaFrames(token: string, fileKey: string): Promise<FigmaFrame[]> {
  const res = await fetch(`https://api.figma.com/v1/files/${fileKey}?depth=2`, {
    headers: { 'X-Figma-Token': token },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Figma API error ${res.status}`);
  }
  const data = await res.json();
  const page = data.document?.children?.[0];
  if (!page) throw new Error('No pages found in Figma file');
  return (page.children ?? [])
    .filter((n: { type: string }) =>
      n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'SECTION'
    )
    .map((n: { id: string; name: string }) => ({ id: n.id, name: n.name }));
}

export async function fetchFigmaFrameAsBase64(
  token: string,
  fileKey: string,
  nodeId: string
): Promise<string> {
  const res = await fetch(
    `https://api.figma.com/v1/images/${fileKey}?ids=${encodeURIComponent(nodeId)}&format=png&scale=2`,
    { headers: { 'X-Figma-Token': token } }
  );
  if (!res.ok) throw new Error(`Figma images API error ${res.status}`);
  const data = await res.json();
  const imageUrl = data.images?.[nodeId];
  if (!imageUrl) throw new Error('No image URL returned by Figma');

  const imgRes = await fetch(imageUrl);
  if (!imgRes.ok) throw new Error('Failed to download Figma frame image');
  const blob = await imgRes.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
